import { computed } from '@angular/core'

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'

import { HarnessConversationDetails } from '@flowx/designer/modules/ai-builder/types/conversation.types'

type HarnessConversationDetailsState = {
  /** What an owner read says beyond the record: where the conversation stands, its plans and
   * its read grants. */
  details: HarnessConversationDetails | null

  /** The requests on their way, so the control that sent one says so and cannot send it twice. */
  openingWriteTarget: boolean
  /** The read grant being revoked. */
  revokingReadGrantId: string | null
}

const initialConversationDetailsState: HarnessConversationDetailsState = {
  details: null,
  openingWriteTarget: false,
  revokingReadGrantId: null,
}

/** What owner reads say beyond the durable record. The session still orchestrates navigation,
 * revocation and reconciliation; this leaf holds the details and the controls' pending flags. */
export const HarnessConversationDetailsStore = signalStore(
  { providedIn: 'root' },
  withState(initialConversationDetailsState),
  withComputed(({ details }) => ({
    /** The read grants the conversation holds. */
    readGrants: computed(() => details()?.grants ?? []),
    /** Every plan the conversation has had, newest first. */
    conversationPlans: computed(() => details()?.plans ?? []),
  })),
  withMethods((store) => ({
    /** Where the conversation stands moves with the turn — its phase, a plan opened or
     * finished — and only an owner read says so. An omitted details field retains that standing. */
    reconcileRead: (details: HarnessConversationDetails | undefined): void => {
      patchState(store, (state) => ({ details: details ?? state.details }))
    },
    replace: (details: HarnessConversationDetails | null): void => patchState(store, { details }),
    reset: (): void => patchState(store, initialConversationDetailsState),
    beginOpeningWriteTarget: (): void => patchState(store, { openingWriteTarget: true }),
    finishOpeningWriteTarget: (): void => patchState(store, { openingWriteTarget: false }),
    beginRevokingReadGrant: (grantId: string): void =>
      patchState(store, { revokingReadGrantId: grantId }),
    finishRevokingReadGrant: (): void => patchState(store, { revokingReadGrantId: null }),
    markReadGrantRevoked: (grantId: string, revokedAt: string): void => {
      patchState(store, (state) => ({
        details: state.details && {
          ...state.details,
          grants: state.details.grants.map((grant) =>
            grant.grant_id === grantId ? { ...grant, revoked_at: revokedAt } : grant
          ),
        },
      }))
    },
  }))
)
